import { useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../services/api';
import LoadingSpinner from '../components/Shared/LoadingSpinner';
import ExecLiveClock from '../components/ContactCenter/ExecLiveClock';
import ExecSlTrendPanel from '../components/ContactCenter/ExecSlTrendPanel';
import PageHeader from '../components/Shared/PageHeader';
import { ExecHeroCard, ExecStripCard } from '../components/ContactCenter/ExecMetricCard';
import { podTone } from '../components/ContactCenter/badges';
import { fmtAsOf, oldestAsOf, fmtCount, fmtMoney, fmtPct, STATUS } from '../components/ContactCenter/format';
import { btn, c, card, tbl } from '../theme';
import { CC_PAGE_MAX_WIDTH } from '../components/ContactCenter/pageWidth';

// Contact Center · Executive Overview — the old app's /exec page.
//
// Migration 053 folded /board, /exec and /vp into one `leadership` view,
// reasoning that they were "one screen at several addresses". They were not,
// so this and the VP page were never built. Migration 080 splits the views
// back out; the Ownership role keeps the Board alone, as the old app had it.
//
// Nothing here recomputes a figure — the server re-shapes the Board's own
// numbers (ccExec) so the two pages cannot drift apart.

const MODES = [['mtd', 'MTD'], ['last-month', 'Last Month'], ['custom', 'Custom']];

const pacificToday = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Los_Angeles' }).format(new Date());
const firstOfMonth = (ymd) => `${ymd.slice(0, 8)}01`;
function lastMonth(today) {
  const [y, m] = today.split('-').map(Number);
  return {
    start: new Date(Date.UTC(y, m - 2, 1)).toISOString().slice(0, 10),
    end: new Date(Date.UTC(y, m - 1, 0)).toISOString().slice(0, 10),
  };
}

// What the delta line on every card is measured against. The server's prior
// window is the same elapsed days of last month for a month to date, so "vs
// Prior Month" is literally true; a custom window compares with the equal
// length window before it, which is not a month.
const priorLabelFor = (kind) => (kind === 'custom' ? 'vs Prior Period' : 'vs Prior Month');

function SectionLabel({ title, sub, right }) {
  return (
    <div style={s.sectionRow}>
      <h2 style={s.section}>{title}<span style={s.sectionSub}>{sub}</span></h2>
      {right}
    </div>
  );
}

export default function ContactCenterExec() {
  const today = pacificToday();
  const [mode, setMode] = useState('mtd');
  const [custom, setCustom] = useState({ start: '', end: '' });
  const [podView, setPodView] = useState('pod');

  const range = mode === 'last-month' ? lastMonth(today)
    : mode === 'custom' ? (custom.start && custom.end ? custom : null)
      : { start: firstOfMonth(today), end: today };

  const q = useQuery({
    queryKey: ['cc-exec', range?.start, range?.end],
    queryFn: () => api.get('/contact-center/exec', { params: range }),
    enabled: !!range,
    refetchInterval: 5 * 60 * 1000,
  });
  const d = q.data;
  const k = d?.cards;
  const priorLabel = priorLabelFor(d?.range.kind);
  const forecastPct = k?.handled.forecast > 0 && k.handled.value != null
    ? Math.round((k.handled.value / k.handled.forecast) * 100) : null;

  // Every "which pods…" button on a driver card asks the same question, and
  // the answer is already on the page: the breakdown at the bottom of it.
  const podsRef = useRef(null);
  const showPods = () => podsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  // The day-by-day panel, as the old page offers it: on the windows where a
  // day is a point and not the whole window. It has no Today preset, so this
  // is every range it has.
  const [slTrend, setSlTrend] = useState(false);

  return (
    <div style={s.page}>
      <PageHeader
        eyebrow="Contact Center"
        title="Executive Overview"
        subtitle={d ? `${d.range.label} · Athens Support Center · phone` : 'Athens Support Center — phone'}
        meta={(
          <span style={s.metaRow}>
            {fmtAsOf(oldestAsOf(d?.asOf)) && <span style={s.chip}>Data as of {fmtAsOf(oldestAsOf(d.asOf))}</span>}
            <ExecLiveClock />
          </span>
        )}
        actions={(
          <div style={s.row}>
            {MODES.map(([key, label]) => (
              <button key={key} type="button" onClick={() => setMode(key)} aria-pressed={mode === key}
                style={mode === key ? s.pillOn : s.pillBtn}>{label}</button>
            ))}
            {mode === 'custom' && (
              <>
                <input id="cc-exec-start" type="date" aria-label="Start date" max={today}
                  value={custom.start} onChange={(e) => setCustom((v) => ({ ...v, start: e.target.value }))} style={s.input} />
                <input id="cc-exec-end" type="date" aria-label="End date" max={today}
                  value={custom.end} onChange={(e) => setCustom((v) => ({ ...v, end: e.target.value }))} style={s.input} />
              </>
            )}
          </div>
        )}
      />

      <p style={s.historyNote}>
        - - - dashed = InContact history (Jan–May 2026). Genesys from June 4, 2026.
      </p>

      {mode === 'custom' && !range && <p style={s.muted}>Choose a start and an end date.</p>}
      {q.isLoading && range && <LoadingSpinner />}
      {q.error && <div style={s.error}>Could not load the Executive Overview: {q.error.message}</div>}
      {d?.range.clamped && (
        <div style={s.notice}>Contact Center data starts on June 4, 2026, when Athens moved to Genesys; the window starts there.</div>
      )}

      {k && (
        <>
          <SectionLabel
            title="Primary drivers"
            sub={`${d.range.label} headline figures · ${d.range.kind === 'custom' ? 'prior-period' : 'prior-month'} comparisons`}
            right={(
              <div style={s.legend}>
                <span style={s.legendItem}><span style={{ ...s.legendDot, background: c.success }} />Hitting target</span>
                <span style={s.legendItem}><span style={{ ...s.legendDot, background: c.danger }} />Missing target</span>
              </div>
            )} />

          <div style={s.drivers}>
            <ExecHeroCard name="Service level" sub="Answered within 60 sec"
              card={k.serviceLevel} kind="pct" betterDir="up" ownerLabel="ASC" priorLabel={priorLabel}
              actionLabel="Which pods are furthest from target?" onAction={showPods}
              secondaryLabel={`${slTrend ? 'Hide' : 'Show'} service level by day`}
              onSecondary={() => setSlTrend((v) => !v)} secondaryPressed={slTrend} />

            {/* Volume is tracked against the uploaded forecast, not a target, so it
                is never "below goal" — it is running at a percentage of forecast. */}
            <ExecHeroCard name="Call volume" sub="Calls handled : inbound + callbacks + VIP"
              card={k.handled} kind="int" betterDir="neutral" priorLabel={priorLabel}
              badgeOverride={{
                label: forecastPct == null ? 'No forecast' : `${forecastPct}% of forecast`,
                color: c.textMuted, bg: c.surfaceSunk,
              }}
              statusOverride={{ label: 'Tracking', color: c.textMuted, bg: c.surfaceSunk }}
              fillOverride={k.handled.forecast > 0 ? Math.min(1, (k.handled.value || 0) / k.handled.forecast) : 0}
              gapOverride={k.handled.forecast != null && k.handled.value != null
                ? k.handled.value - k.handled.forecast : null}
              rowsOverride={[
                [`Forecast : ${d.range.label}`, fmtCount(k.handled.forecast)],
                [`% of ${d.range.label} forecast`, forecastPct == null ? '—' : `${forecastPct}%`],
                ['YTD*', fmtCount(k.handled.ytd)],
              ]}
              caption={k.handled.forecastCoveragePct != null && k.handled.forecastCoveragePct < 100
                ? `Compared on the ${k.handled.forecastCoveragePct}% of days the forecast covers, on both sides.`
                : null}
              actionLabel="Which pods drive our call volume?" onAction={showPods} />

            <ExecHeroCard name="AHT" sub="Avg handle time" card={k.aht} kind="time" betterDir="down"
              ownerLabel="ASC" priorLabel={priorLabel}
              actionLabel="Which pods have the longest handle time?" onAction={showPods} />
          </div>

          {/* Directly under the drivers, where the card that opened it is —
              not at the bottom of the page, which would make the button feel
              like it had done nothing. */}
          {slTrend && (
            <ExecSlTrendPanel daily={d.daily} target={d.slTarget} today={today}
              rangeLabel={d.range.label} onClose={() => setSlTrend(false)} />
          )}

          <SectionLabel title="Supporting metrics" sub={d.range.label} />
          <div style={s.supporting}>
            <ExecStripCard name="ASA" sub="Avg speed of answer" card={k.asa} kind="time"
              betterDir="down" ownerLabel="ASC" priorLabel={priorLabel}
              caption="Voice + VIP + Callback, organization-wide" />
            <ExecStripCard name="Avg hold" sub="Avg hold per hold event" card={k.hold} kind="time"
              betterDir="down" ownerLabel="ASC" priorLabel={priorLabel} noTarget
              caption="per hold event · Voice + VIP" />
            <ExecStripCard name="Transfer rate" sub="Calls passed to someone else" card={k.transfer}
              kind="pct" betterDir="down" ownerLabel="ASC" priorLabel={priorLabel}
              caption="distinct conversations, organization-wide" />
            <ExecStripCard name="Abandonment" sub="Callers who hung up waiting" card={k.abandon}
              kind="pct" betterDir="down" ownerLabel="ASC" priorLabel={priorLabel} />
            {/* The caveat hangs BELOW the card, as it does on the old page.
                Inside it, it made the card the tallest in the row and the other
                four stretched to match, leaving a band of white under each. */}
            <div style={s.stripStack}>
              <ExecStripCard name="Cost / call" sub="Fully-loaded cost per call" card={k.costPerCall}
                kind="money" betterDir="down" ownerLabel="ASC" priorLabel={priorLabel} noTarget />
              <div style={s.modelNote}>Modeled from AHT and platform cost — not a Finance actual.</div>
            </div>
          </div>

          <section ref={podsRef} style={s.card} aria-label="Pod breakdown">
            <div style={s.podHead}>
              <h2 style={s.h2}>Pod breakdown <span style={s.sectionSub}>{d.range.label}</span></h2>
              <span style={s.seg} role="group" aria-label="Group by">
                {[['pod', 'POD view'], ['yard', 'YARD view']].map(([id, label]) => (
                  <button key={id} type="button" aria-pressed={podView === id} onClick={() => setPodView(id)}
                    style={podView === id ? s.segOn : s.segOff}>{label}</button>
                ))}
              </span>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={s.table}>
                <thead>
                  <tr>
                    <th style={s.th}>{podView === 'yard' ? 'Yard' : 'Pod'}</th>
                    <th style={s.thNum}>Service level</th>
                    <th style={s.thNum}>Repeat % · {d.repeatWindowDays}d</th>
                    <th style={s.thNum}>% of volume</th>
                    <th style={s.th}>Top city</th>
                    <th style={s.th}>Call driver</th>
                    <th style={s.thNum}>Cost / call</th>
                  </tr>
                </thead>
                <tbody>
                  {d.pods.map((p) => {
                    const tone = STATUS.serviceLevel(p.serviceLevel);
                    return (
                      <tr key={p.pod}>
                        <td style={s.td}>
                          <span style={{ ...s.podBadge, background: podTone(p.pod).bg, color: podTone(p.pod).fg }}>
                            <span style={{ ...s.podDot, background: podTone(p.pod).dot }} />
                            {podView === 'yard' ? (p.yard ?? p.pod) : p.pod}
                          </span>
                        </td>
                        <td style={{ ...s.num, color: tone === 'good' ? c.successText : tone === 'bad' ? c.dangerText : c.warningText, fontWeight: 600 }}>
                          {fmtPct(p.serviceLevel)}
                        </td>
                        <td style={s.num}>{fmtPct(p.repeatPct)}</td>
                        <td style={s.num}>{fmtPct(p.volumeSharePct)}</td>
                        <td style={s.td}>{p.topCity ?? <span style={{ color: c.textFaint }}>—</span>}</td>
                        <td style={s.td}>{p.callDriver ?? <span style={{ color: c.textFaint }}>—</span>}</td>
                        <td style={s.num}>{fmtMoney(p.costPerCall)}</td>
                      </tr>
                    );
                  })}
                  {!d.pods.length && <tr><td colSpan={7} style={s.empty}>No pod figures for this window.</td></tr>}
                </tbody>
              </table>
            </div>
            <p style={s.foot}>
              Service level is Voice and VIP. Repeat % is a rolling {d.repeatWindowDays}-day window
              ({d.repeatWindow.from} to {d.repeatWindow.to}), not the period above — a repeat caller is a
              this-week phenomenon and a month-to-date figure buries it.
              {!d.repeatAvailable && ' The repeat report could not be built for this window.'}
              {' '}% of volume is each pod&rsquo;s share of the pods added together, so the column totals 100%;
              the organization&rsquo;s own count is lower, because a call reaching two pods is one call.
              YARD view relabels each pod by the yard it belongs to.
            </p>
          </section>
        </>
      )}
    </div>
  );
}

const s = {
  metaRow:      { display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' },
  page:       { maxWidth: CC_PAGE_MAX_WIDTH, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '14px' },
  row:        { display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' },
  sectionRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    gap: '16px', margin: '10px 0 1px' },
  section:    { fontSize: '13px', fontWeight: 750, letterSpacing: '0.08em', textTransform: 'uppercase',
    color: c.text, margin: 0 },
  sectionSub: { fontWeight: 400, fontSize: '12.5px', letterSpacing: 0, textTransform: 'none',
    color: c.textFaint, marginLeft: '10px' },
  legend:     { display: 'flex', gap: '16px', alignItems: 'center' },
  legendItem: { display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: c.textMuted },
  legendDot:  { width: '9px', height: '9px', borderRadius: '50%' },
  // Three across and five across, the old page's grids. auto-fit keeps them
  // from overflowing a narrow window, which fixed columns would.
  drivers:    { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px' },
  supporting: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
    gap: '16px', alignItems: 'start' },
  stripStack: { display: 'flex', flexDirection: 'column', minWidth: 0 },
  modelNote:  { marginTop: '6px', border: `1px solid ${c.warning}33`, background: c.warningTint,
    color: c.warningText, borderRadius: '8px', padding: '7px 10px', fontSize: '11px',
    fontWeight: 600, lineHeight: 1.35 },
  historyNote:{ fontSize: '11px', color: c.textFaint, margin: '-4px 0 0' },
  card:       { ...card.base, padding: '14px 18px 12px' },
  podHead:    { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '8px' },
  h2:         { fontSize: '13px', fontWeight: 650, color: c.text, margin: 0 },
  seg:        { display: 'inline-flex', gap: '2px', background: c.surfaceSunk, borderRadius: '8px', padding: '3px' },
  segOff:     { all: 'unset', cursor: 'pointer', padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: 700, color: c.textMuted },
  segOn:      { all: 'unset', cursor: 'pointer', padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: 700, color: c.textStrong, background: c.surface, boxShadow: '0 1px 2px rgba(15,23,42,0.08)' },
  table:      { width: '100%', borderCollapse: 'collapse', fontSize: '12.5px' },
  th:         { ...tbl.th, textAlign: 'left', whiteSpace: 'nowrap' },
  thNum:      { ...tbl.thR, whiteSpace: 'nowrap' },
  td:         { ...tbl.td, color: c.textStrong, whiteSpace: 'nowrap' },
  num:        { ...tbl.tdR, color: c.textStrong },
  podBadge:   { display: 'inline-flex', alignItems: 'center', gap: '5px', borderRadius: '4px', padding: '1px 6px', fontSize: '10.5px', fontWeight: 600 },
  podDot:     { width: '6px', height: '6px', borderRadius: '2px', flexShrink: 0 },
  empty:      { ...tbl.td, textAlign: 'center', color: c.textMuted, padding: '20px' },
  foot:       { fontSize: '11px', color: c.textMuted, margin: '10px 0 0', lineHeight: 1.5 },
  pillBtn:    { ...btn.secondaryXs },
  pillOn:     { ...btn.secondaryXs, background: c.navy, color: '#fff', borderColor: c.navy },
  input:      { fontSize: '12px', padding: '5px 8px', border: `1px solid ${c.borderStrong}`, borderRadius: '6px', fontFamily: 'inherit', color: c.textStrong, background: c.surface },
  chip:       { fontSize: '11px', color: c.textMuted },
  notice:     { fontSize: '12px', color: c.warningText, background: c.warningTint, padding: '8px 12px', borderRadius: '8px' },
  error:      { fontSize: '13px', color: c.dangerText },
  muted:      { fontSize: '12px', color: c.textMuted, margin: 0 },
};
